<template>
  <AppContentLoader v-if="isPreLoading" />

  <span
    v-else-if="error"
    class="h-full w-full font-semibold text-black text-lg flex items-center justify-center"
  >
    {{ error }}
  </span>

  <div
    v-else
    class="content-to-print h-full"
  >
    <CoverPage :pdf-data="pdfData" />

    <IndexPage
      :all-renderable-sections="allRenderableSections"
      @index-clicked="goToSection"
    />

    <!-- sections -->
    <template
      v-for="(section, idx) in allRenderableSections"
      :key="idx"
    >
      <section
        :id="section.id"
        :class="{
          'page': !section.isCategory,
          'has-content': section.content?.length,
          'category': section.isCategory,
          'break-before': section.breakBefore,
          'subsection': section.level > 0
        }"
        :data-page-root-id="section.rootId"
        :data-page-parent-id="section.parent?.id"
        :data-page-id="section.id"
        :data-label="section.label"
      >
        <!--custom header for sections so we can get current section context-->
        <CustomHeader
          :pdf-data="pdfData"
          :section="section"
        />

        <template v-if="!(section.isCategory && section.level > 0)">
          <h1 v-if="section.level === 0 && section.label">
            {{ section.label }}
          </h1>

          <h2 v-else-if="section.level === 1 && section.label && section.rootId !== 'situational-analysis'">
            {{ section.label }}
          </h2>

          <h3 v-else-if="section.level === 2 && section.label">
            {{ section.label }}
          </h3>

          <template v-if="section.content">
            <template
              v-for="(contentItem, cIdx) in section.content.flat()"
              :key="cIdx"
            >
              <template v-if="contentItem.type === 'heading'">
                <h3
                  v-if="section.level > 0 || section.id === 'development-strategies' || section.id === 'situational-analysis'"
                  class="heading"
                >
                  {{ contentItem.value }}
                </h3>

                <h2
                  v-else
                  class="heading"
                >
                  {{ contentItem.value }}
                </h2>
              </template>

              <!--wizywig content-->
              <div
                v-if="contentItem.type === 'tinymce'"
                class="tinymce-content"
                v-html="domPurify.sanitize(contentItem.value)"
              />

              <div
                v-if="contentItem.type === 'powerbi'"
                :data-powerbi-page-id="contentItem.pageId"
                class="powerbi-container w-full h-full"
              />

              <p
                v-else-if="contentItem.type === 'empty'"
                class="empty-content"
              >
                No information has been captured for this section.
              </p>

              <DocumentLink
                v-else-if="contentItem.type === 'link'"
                :content-item="contentItem"
              />

              <SignatureBlock
                v-else-if="contentItem.type === 'official'"
                :content-item="contentItem"
              />

              <!--tables-->
              <template v-else-if="contentItem.type === 'table'">
                <ListOfAbbreviationsTable
                  v-if="section.id === 'list-of-abbreviations'"
                  :content-item="contentItem"
                />

                <ListOfFiguresTable
                  v-if="section.id === 'list-of-figures'"
                  :content-item="contentItem"
                />

                <DevelopmentStrategiesTable
                  v-if="section.id === 'development-strategies'"
                  :content-item="contentItem"
                />

                <!--local economic development has basic content-->

                <ProjectsTable
                  v-if="section.rootId === 'implementation-plan'"
                  :content-item="contentItem"
                />

                <!--financial tables-->
                <template v-if="section.rootId === 'financial-plan'">
                  <BudgetTable
                    v-if="contentItem.budgetType"
                    :plan="plan"
                    :budget-type="contentItem.budgetType"
                  />

                  <FinancialPlanDefaultTable
                    v-else
                    :plan="plan"
                    :store-key="contentItem.storeKey"
                  />
                </template>

                <PerformanceTargetsTable
                  v-if="section.id === 'performance-targets'"
                  :content-item="contentItem"
                />
              </template>
            </template>
          </template>
        </template>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Previewer } from 'pagedjs';
import { HttpStatusCode } from 'axios';
import { AppContentLoader } from '@/components/app';

import {
  CoverPage,
  IndexPage,
  CustomHeader,
  SignatureBlock,
  DocumentLink,
  // tables
  BudgetTable,
  DevelopmentStrategiesTable,
  FinancialPlanDefaultTable,
  ListOfAbbreviationsTable,
  ListOfFiguresTable,
  PerformanceTargetsTable,
  ProjectsTable,
} from '@/views/authenticated/plans/pdfPreviewSections';

import { buildPdfContent, flattenSections } from '@/composables/buildPdfContent';
import '@/composables/repeatingTableHeader';
import { createPowerBiLoader } from '@/composables/powerBiLoader';

import { useHttp, useDomPurify } from '@/composables';
import { MunicipalityType, planDuration } from '@/const';

const route = useRoute();
const domPurify = useDomPurify();

const plan = ref();
const pdfData = ref({});
const settingsDictionary = ref({});
const figuresList = ref([]);

const frameworksLookup = ref(new Map());
const goalsLookup = ref(new Map());

const pagedFlow = ref(null);
const allRenderableSections = ref([]);

const isPreLoading = ref(true);
const error = ref(null);
const isLoadingPreview = ref(false);

const urlSearchParams = new URLSearchParams(window.location.search);
const isPrintMode = urlSearchParams.get('type') === 'print';

let landscapePageStyle = null;

const municipalitySettingsPrefix = computed(() =>
  pdfData.value.municipality?.type === MunicipalityType.DistrictMunicipality ? 'district' : 'municipality',
);

const { loadPowerBi, cleanupPowerBi } = createPowerBiLoader({
  pdfData, isPrintMode, municipalitySettingsPrefix, settingsDictionary, error,
});

// Function to map the IDs passed in the frameworkAlignments to the actual entity
const fetchFrameworkDetails = async (strategies) => {
  if (!strategies || !strategies.length) return;

  const frameworkIds = new Set();
  strategies.forEach(strategy => {
    if (Array.isArray(strategy.frameworkAlignments)) {
      strategy.frameworkAlignments.forEach(alignment => {
        if (alignment.frameworkId) frameworkIds.add(alignment.frameworkId);
      });
    }
  });

  if (frameworkIds.size === 0) return;

  const promises = [];

  // Fetch Framework details (for the Name)
  frameworkIds.forEach(id => {
    promises.push(
      useHttp().get(`/util/frameworks/${id}`).then(response => {
        if (response.statusCode === 200) {
          frameworksLookup.value.set(response.data.id, response.data.name);
        } else {
          error.value = 'Failed to load pdf.';
          return;
        }
      }),
    );

    // Fetch Goals for this Framework (because /frameworks/{id} doesn't include them)
    promises.push(
      useHttp().get(`/util/frameworks/${id}/goals`).then(response => {
        if (response.statusCode === 200 && response.data.items) {
          // Recursive helper to map Goal/Subgoal IDs to Names
          const processGoals = (goalList) => {
            goalList.forEach(goal => {
              goalsLookup.value.set(goal.id, { name: goal.name });
              // Your API structure has 'subgoals' array inside goals
              if (goal.subgoals && goal.subgoals.length) {
                processGoals(goal.subgoals);
              }
            });
          };
          processGoals(response.data.items);
        } else {
          error.value = 'Failed to load pdf.';
        }
      }),
    );
  });

  // Wait for all API calls to finish
  await Promise.all(promises);
};

const loadAnnexures = async () => {
  const response = await useHttp().get(`/municipalities/${route.params.municipality}/plan/annexures`);

  if (response.statusCode === HttpStatusCode.Ok) {
    pdfData.value.annexures = response.data.items || [];
  } else {
    error.value = 'Failed to load pdf.';
  }
};

const loadPlan = async () => {
  // Load Settings
  const settingsResponse = await useHttp().get('/util/settings');

  if (settingsResponse.statusCode === HttpStatusCode.Ok) {
    settingsDictionary.value = settingsResponse.data.items.reduce((acc, item) => {
      acc[item.key] = item.values;
      return acc;
    }, {});
  } else {
    error.value = 'Failed to load pdf.';
    return;
  }

  // Load plan
  const response = await useHttp().get(`/util/municipalities/${route.params.municipality}/plan`);

  if (response.statusCode === HttpStatusCode.Ok) {
    const { content, ...metaData } = response.data;
    plan.value = response.data;

    pdfData.value = metaData;
    pdfData.value.theme = content.headingColour || '#2E2F90';
    pdfData.value.duration = planDuration;
    pdfData.value.lastPublishedAt = plan.value?.lastPublishedAt;

    await loadAnnexures();
    await fetchFrameworkDetails(content.developmentStrategies);

    pdfData.value.contentSections = buildPdfContent(
      content,
      {
        pdfData,
        settingsDictionary,
        figuresList,
        frameworksLookup,
        goalsLookup,
        municipalitySettingsPrefix,
      },
    );
  } else {
    error.value = 'Failed to load pdf.';
  }
};

const applyPrintStyles = () => {
  const pagedJsContainer = document.querySelector('.pagedjs_pages');
  if (pagedJsContainer && !pagedJsContainer.classList.contains('print')) {
    pagedJsContainer.classList.add('print');
  }
};

const removePrintStyles = () => {
  if (!isPrintMode) {
    const pagedJsContainer = document.querySelector('.pagedjs_pages');
    if (pagedJsContainer) {
      pagedJsContainer.classList.remove('print');
    }
  }
};

onMounted(async () => {
  // Listeners for printing
  window.addEventListener('beforeprint', applyPrintStyles);
  window.addEventListener('afterprint', removePrintStyles);

  await loadPlan();

  if (error.value) {
    isPreLoading.value = false;
    return;
  }

  document.body.style.setProperty('--theme-color', pdfData.value.theme);

  await waitForImagesToLoad();

  allRenderableSections.value = flattenSections(pdfData.value.contentSections, figuresList, 0, []);

  isPreLoading.value = false;
  isLoadingPreview.value = true;

  // Ensure Vue has fully rendered all sections before Paged.js starts its layout pass.
  await nextTick();

  const previewer = new Previewer();
  pagedFlow.value = await previewer.preview();

  // PagedJS turns named @page sizes into classes and gives the browser a single portrait @page, so
  // printing the preview with Ctrl+P cuts the landscape pages off. Declaring the landscape page to the
  // browser after PagedJS has run keeps them whole. Puppeteer sets its own page size, so skip it there.
  if (!isPrintMode) {
    landscapePageStyle = document.createElement('style');
    landscapePageStyle.textContent = `
      @page landscape { size: A4 landscape; margin: 0; }
      .pagedjs_performanceTargetsPage_page { page: landscape; }
      .pagedjs_performanceTargetsPage_page,
      .pagedjs_performanceTargetsPage_page .pagedjs_sheet {
        width: 297mm !important;
        height: 210mm !important;
      }
    `;
    document.head.appendChild(landscapePageStyle);
  }

  // target-counter can't reliably resolve dynamically-assigned IDs (set via JS in
  // flattenSections), so page numbers are injected programmatically after layout.
  const sheets = Array.from(document.querySelectorAll('.pagedjs_sheet'));
  const injectPageNumbers = (selector) => {
    document.querySelectorAll(selector).forEach(link => {
      const targetId = link.getAttribute('href')?.replace('#', '');
      if (!targetId) return;
      const targetEl = document.getElementById(targetId);
      if (!targetEl) return;
      const sheet = targetEl.closest('.pagedjs_sheet');
      if (!sheet) return;
      link.setAttribute('data-page', sheets.indexOf(sheet) + 1);
    });
  };

  injectPageNumbers('[data-page-id="list-of-figures"] .td:last-child a');
  injectPageNumbers('.index-page-page-link-number');

  // get the container of all pagedjs pages so we can apply styles to it based on the hash
  const pagedJsContainer = document.querySelector('.pagedjs_pages');

  await loadPowerBi(pagedJsContainer);

  if (isPrintMode) {
    const pages = document.querySelectorAll('.content-to-print');
    const pageOrientations = [];

    pagedJsContainer.classList.add('print');

    // make an array of each pages orientation
    pages.forEach((page) => {
      const rect = page.getBoundingClientRect();

      if (rect.width > rect.height) {
        pageOrientations.push('landscape');
      } else {
        pageOrientations.push('portrait');
      }
    });

    // expose array of orientations to Puppeteer so it can choose the correct pages from each buffer
    window.pageOrientations = pageOrientations;

    // Map all pages to find their order
    const sheets = Array.from(document.querySelectorAll('.pagedjs_sheet'));

    // Find all internal links (cuz pdf-lib destroys them when it merges pages together)
    const linksToInject = [];
    const internalLinks = document.querySelectorAll('.pagedjs_pages a[href^="#"]');

    internalLinks.forEach(link => {
      // Find the link's physical location (Source Page)
      const linkSheet = link.closest('.pagedjs_sheet');
      if (!linkSheet) return;

      const sourcePageIndex = sheets.indexOf(linkSheet);

      // Find the target's physical location (Destination Page)
      const hash = link.getAttribute('href'); // e.g., "#introduction"
      if (!hash) return;

      // Sanitize hash to find ID
      const targetId = hash.replace('#', '');
      const targetEl = document.getElementById(targetId);

      // If target exists in the print DOM
      if (targetEl) {
        const targetSheet = targetEl.closest('.pagedjs_sheet');
        if (targetSheet) {
          const targetPageIndex = sheets.indexOf(targetSheet);

          // Calculate Coordinates as Percentages (0 to 1)
          const linkRect = link.getBoundingClientRect();
          const sheetRect = linkSheet.getBoundingClientRect();

          // Relative position inside the sheet
          const relativeX = linkRect.left - sheetRect.left;
          const relativeY = linkRect.top - sheetRect.top;

          linksToInject.push({
            sourcePageIndex, // Which page is the link ON?
            targetPageIndex, // Which page does it go TO?
            // Save coordinates as percentages of the page size
            xPct: relativeX / sheetRect.width,
            yPct: relativeY / sheetRect.height,
            wPct: linkRect.width / sheetRect.width,
            hPct: linkRect.height / sheetRect.height,
          });
        }
      }
    });

    // expose array of links to Puppeteer so it can create new links for all the pages after pdf-lib merges them
    window.pdfLinks = linksToInject;

    isLoadingPreview.value = false;

    await nextTick();
    window.pdfReady = true;
  }
});

onUnmounted(() => {
  window.removeEventListener('beforeprint', applyPrintStyles);
  window.removeEventListener('afterprint', removePrintStyles);
  landscapePageStyle?.remove();
  cleanupPowerBi();
});

// necessary cuz images need to be loaded before paged.js runs
function waitForImagesToLoad() {
  const images = Array.from(document.images);

  const imagePromises = images.map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });

  const timeoutPromise = new Promise(resolve => setTimeout(resolve, 5000));

  // Wait for images OR timeout
  return Promise.race([Promise.all(imagePromises), timeoutPromise]);
}

// used by index-page
function goToSection(id) {
  if (!pagedFlow.value) return;
  const section = allRenderableSections.value.find(s => s.id === id);
  if (section?.page) pagedFlow.value.goTo(section.page);
}
</script>

<style lang="scss">
@import '@/assets/styles/components/plan-pdf-preview';
</style>
